import { Users } from 'lucide-react';
import { NoOpsSelected } from '../../../../components';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import {
  Tabs,
  TabsContent,
  PageTabsList,
  PageTabsTrigger,
} from '../../../../components/ui/tabs';
import { useAuth } from '../../../../auth/AuthContext';
import { useOps } from '../../../../hooks/useOps';
import {
  BlockedUsersTable,
  BulkOperations,
  IndividualSearch,
  RegistrationDetails,
  RegistrationsList,
} from './components';
import { useBlockedUsers, useBulkOperations, useEntregoSearch } from './hooks';

export function RomaneioCadastroEntrego() {
  const { currentOps } = useOps();
  const { hasPermission } = useAuth();
  // Gates the bulk block/unblock action (backend enforces too).
  const canBulkUpdate = hasPermission('entregoCadastroBulkUpdate', currentOps || undefined);
  const entregoSearch = useEntregoSearch();
  const bulkOperations = useBulkOperations();
  const blockedUsers = useBlockedUsers();

  if (!currentOps) {
    return (
      <NoOpsSelected
        message="Selecione uma operação para gerenciar cadastros de entregadores."
        className="animate-in fade-in-50 duration-300"
      />
    );
  }

  const showList =
    entregoSearch.registrations.length > 1 && !entregoSearch.registration;
  const showDetails = entregoSearch.registration !== null;

  return (
    <div className="space-y-6 animate-in fade-in-50 duration-300">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight text-[hsl(210,50%,10%)]">
            <Users className="h-8 w-8" />
            Gestão de Cadastros
          </h1>
          <p className="text-[hsl(215,16%,47%)]">
            Gerencie o status de cadastros de entregadores individualmente ou em
            lote
          </p>
        </div>
      </div>

      {entregoSearch.searchError && (
        <Alert
          variant="destructive"
          className="animate-in slide-in-from-top-2 fade-in-50 duration-200"
        >
          <AlertDescription>{entregoSearch.searchError}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="individual" className="w-full">
        <PageTabsList>
          <PageTabsTrigger value="individual">Busca Individual</PageTabsTrigger>
          <PageTabsTrigger value="bulk">Operações em Lote</PageTabsTrigger>
          <PageTabsTrigger value="blocked">Entregadores Bloqueados</PageTabsTrigger>
        </PageTabsList>

        <TabsContent
          value="individual"
          className="mt-6 space-y-4 animate-in fade-in-50 duration-300"
        >
          <IndividualSearch
            onSearch={entregoSearch.search}
            isSearching={entregoSearch.isSearching}
          />

          {showList && (
            <RegistrationsList
              registrations={entregoSearch.registrations}
              onSelect={entregoSearch.selectRegistration}
            />
          )}

          {showDetails && entregoSearch.registration && (
            <RegistrationDetails
              registration={entregoSearch.registration}
              isUpdating={entregoSearch.isUpdating}
              updateSuccess={entregoSearch.updateSuccess}
              updateError={entregoSearch.updateError}
              onUpdateStatus={entregoSearch.updateStatus}
              onClose={entregoSearch.clearSearch}
            />
          )}
        </TabsContent>

        <TabsContent
          value="bulk"
          className="mt-6 animate-in fade-in-50 duration-300"
        >
          <BulkOperations
            file={bulkOperations.file}
            parseResult={bulkOperations.parseResult}
            operationResult={bulkOperations.operationResult}
            isParsing={bulkOperations.isParsing}
            isProcessing={bulkOperations.isProcessing}
            parseError={bulkOperations.parseError}
            processError={bulkOperations.processError}
            onFileSelect={bulkOperations.handleFileSelect}
            onProcess={bulkOperations.processOperation}
            onReset={bulkOperations.reset}
            onDownloadTemplate={bulkOperations.generateCsvTemplate}
            canProcess={canBulkUpdate}
          />
        </TabsContent>

        <TabsContent
          value="blocked"
          className="mt-6 animate-in fade-in-50 duration-300"
        >
          <BlockedUsersTable
            users={blockedUsers.users}
            pagination={blockedUsers.pagination}
            isLoading={blockedUsers.isLoading}
            isError={blockedUsers.isError}
            error={blockedUsers.error}
            isUpdating={blockedUsers.isUpdating}
            filters={blockedUsers.filters}
            onPageChange={blockedUsers.goToPage}
            onApplyFilters={blockedUsers.applyFilters}
            onClearFilters={blockedUsers.clearFilters}
            onToggleBlockStatus={blockedUsers.toggleBlockStatus}
            onRefetch={blockedUsers.refetch}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
